// props explained in further detail at https://github.com/securingsincity/react-ace/blob/master/docs/Ace.md
export type AceEditorProps = {
  /** A debounce delay period for the onChange event */
  debounceChangePeriod?: number;
  /** Properties to apply directly to the Ace editor instance */
  editorProps?: AceEditorEditorProps;
  /** pixel value for font-size */
  fontSize?: number;
  /** CSS value for height */
  height?: string;
  /** Language for parsing and code highlighting */
  /** javascript, java, python, xml, ruby, sass, markdown, mysql, json, html, handlebars, golang, csharp, elixir, typescript, css */
  mode?: string;
  /** Unique id to be used for the editor */
  name?: string;
  /** Callback triggered by editor blur event */
  onBlur?: () => void;
  /** Callback that occurs on document change */
  onChange?: (value: any) => void;
  /** Callback triggered by editor focus event */
  onFocus?: () => void;
  /** Callback triggered by editor input event */
  onInput?: () => void;
  /** Callback triggered when the editor loads */
  onLoad?: (editorInstance: any) => void;
  /** Callback that occurs on paste */
  onPaste?: (value: any) => void;
  /** Callback that occurs on validate (returns an array of validation errors based on mode) */
  onValidate?: (value: AceEditorAnnotation[]) => void;
  /** text to show in editor on intital display */
  placeholder?: string;
  readOnly?: boolean;
  showGutter?: boolean; // (defaults to true)
  /** show/hide printMarginColumn (vertical ruler): printMarginColumn defaults to 80 */
  showPrintMargin?: boolean;
  /** Theme style to use */
  theme?: AceEditorTheme | '';
  /** value poulated in the code highlighter */
  value?: string;
  /** CSS value for width */
  width?: string;
  /** Wrapping lines */
  wrapEnabled?: boolean;
};

// https://github.com/ajaxorg/ace/wiki/Configuring-Ace
export type AceEditorEditorProps = {
  // this is needed if editor is inside scrollable page
  autoScrollEditorIntoView?: boolean; // (defaults to false)
  behavioursEnabled?: boolean;
  // copy/cut the full line if selection is empty, defaults to false
  copyWithEmptySelection?: boolean;
  cursorStyle?: 'ace' | 'slim' | 'smooth' | 'wide';
  enableMultiselect?: boolean; // # on by default
  highlightActiveLine?: true | false;
  highlightSelectedWord?: true | false;
  mergeUndoDeltas?: false | true | 'always';
  navigateWithinSoftTabs?: boolean; // (defaults to false)
  readOnly?: true | false;
  selectionStyle?: 'line' | 'text';
  useSoftTabs?: boolean; // (defaults to false)
  wrapBehavioursEnabled?: boolean;

  $blockScrolling?: number;

  // following options require ext-language_tools.js
  enableBasicAutocompletion?: boolean;
  enableLiveAutocompletion?: boolean;
  enableSnippets?: boolean;
  // the following option requires ext-emmet.js and the emmet library
  enableEmmet?: boolean;
  // the following option requires ext-elastic_tabstops_lite.js
  useElasticTabstops?: boolean;
};

export enum AceEditorTheme {
  Chrome = 'chrome',
  Clouds = 'clouds',
  Github = 'github',
  Monokai = 'monokai',
  Twilight = 'twilight',
}

export type AceEditorAnnotation = {
  row?: number;
  column?: number;
  text: string;
  type: string;
};
